import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationsContactData } from '~/api/organizationContactData';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, ClipSpinner } from '~/components/ui';
import { AddSellerContactPanel } from '~/components/common/AddSellerContactPanel';
import { sellerLabel, toastGraphQLError } from '~/utils';
import {
  contactLabel,
  type Contact,
  type RecipientOrganization,
} from '~/utils/contact';

type PrimaryContactModalProps = Readonly<{
  onClose: () => void;
  sellingOrganization: RecipientOrganization;
  initialContactId?: string;
  subtitle: string;
  isSaving: boolean;
  save: (userId: string) => Promise<unknown>;
}>;

export function PrimaryContactModal({
  onClose,
  sellingOrganization,
  initialContactId,
  subtitle,
  isSaving,
  save,
}: PrimaryContactModalProps) {
  const { t } = useTranslation('general');
  const radioName = useId();
  const sellerName = sellerLabel(sellingOrganization);
  const [chosenContactId, setChosenContactId] = useState(
    initialContactId ?? ''
  );
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const contactData = useOrganizationsContactData([sellingOrganization.id]);
  const contacts: Contact[] =
    contactData.data?.organizationsByIds[0]?.sellerContacts ?? [];
  const isChosenContactListed = contacts.some(
    (contact) => contact.id === chosenContactId
  );
  const hasNoContacts = contactData.data !== undefined && contacts.length === 0;

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async () => {
    try {
      await save(chosenContactId);
      onClose();
    } catch (error) {
      toastGraphQLError(error);
    }
  };

  const contactList = (
    <fieldset>
      <legend className="sr-only">
        {t('primary_contact_modal.title', { sellerName })}
      </legend>
      <ul className="space-y-1">
        {contacts.map((contact) => {
          const radioId = `${radioName}-${contact.id}`;
          return (
            <li key={contact.id} className="px-1 py-2 flex items-center gap-2">
              <input
                id={radioId}
                type="radio"
                name={radioName}
                className="h-4 w-4 cursor-pointer"
                checked={chosenContactId === contact.id}
                onChange={() => setChosenContactId(contact.id)}
              />
              <label
                htmlFor={radioId}
                className="text-sm w-full min-w-0 truncate cursor-pointer"
              >
                {contactLabel(contact)}
              </label>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );

  const renderBody = () => {
    if (contactData.isLoading) {
      return (
        <div className="px-6 py-4">
          <ClipSpinner />
        </div>
      );
    }
    if (contactData.error && !contactData.data) {
      return (
        <p className="px-6 py-4 text-sm text-error-600">
          {t('primary_contact_modal.load_failed')}
        </p>
      );
    }
    return (
      <AddSellerContactPanel
        organizationId={sellingOrganization.id}
        testId="add-contact-button"
        disabled={isSaving}
        initiallyOpen={hasNoContacts}
        focusAfterSave={saveButtonRef}
        onAdded={(contact) => setChosenContactId(contact.id)}
        onCancel={hasNoContacts ? handleClose : undefined}
      >
        {({ trigger, form }) => (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-3">
              {hasNoContacts && (
                <p className="text-sm text-neut-sec">
                  {t('primary_contact_modal.no_contacts')}
                </p>
              )}
              {form ?? contactList}
            </div>
            {!form && (
              <div className="px-6 py-4 border-t border-neut-border flex flex-wrap items-center gap-3 shrink-0">
                {trigger}
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isSaving}
                >
                  {t('primary_contact_modal.cancel')}
                </Button>
                <Button
                  ref={saveButtonRef}
                  variant="primary"
                  onClick={() => void handleSave()}
                  loading={isSaving}
                  disabled={isSaving || !isChosenContactListed}
                  data-testid="save-primary-contact-button"
                >
                  {t('primary_contact_modal.save')}
                </Button>
              </div>
            )}
          </>
        )}
      </AddSellerContactPanel>
    );
  };

  return (
    <Modal isOpen onClose={handleClose}>
      <div
        className="bg-neut-light rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        data-testid="primary-contact-modal"
      >
        <ModalHeader
          title={t('primary_contact_modal.title', { sellerName })}
          description={subtitle}
          onClose={handleClose}
          closeDisabled={isSaving}
        />
        {renderBody()}
      </div>
    </Modal>
  );
}
